import React from "react";
import { Link, useNavigate } from "react-router";
import { getRelayErrorMessage } from "src/common/utils";

import { Button } from "./button";
import { Card } from "./card";

interface GraphQLErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface GraphQLErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class GraphQLErrorBoundary extends React.Component<
  GraphQLErrorBoundaryProps,
  GraphQLErrorBoundaryState
> {
  constructor(props: GraphQLErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): GraphQLErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("GraphQL error boundary caught error:", error, errorInfo);  
  }

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return <GraphQLErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

interface GraphQLErrorFallbackProps {
  error: Error | null;
}

const GraphQLErrorFallback: React.FC<GraphQLErrorFallbackProps> = ({ error }) => {
  const navigate = useNavigate();
  
  // Get a user-friendly error message
  const errorMessage = error ? getRelayErrorMessage(error) : "An unexpected error occurred";
  
  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <Card className="max-w-md p-6 bg-popover text-foreground border-border">
        <div className="flex flex-col space-y-4">
          <h2 className="text-xl font-semibold text-red-400">Server Error</h2>
          <p className="text-muted-foreground overflow-y-auto max-h-[50vh]">{errorMessage}</p>
          <div className="flex flex-row space-x-3 pt-2">
            <Button asChild>
              <Link to="/">Go Back</Link>
            </Button>
            <Button variant="secondary" onClick={() => navigate(0)}>
              Retry
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GraphQLErrorBoundary;